import {Inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {Observer} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import "rxjs/add/operator/share";
import {AudioFormatService} from "./audioformat.service";
import {MICROPHONECHECK_STATUS, MicrophoneCheckEvent} from "../../event/MicrophoneCheckEvent";
import {EventBus} from "../../event/EventBus";
import {PauseSelectionEvent} from "../../event/ReadAndRecordEvents";
import {ClientConfiguration} from "../configuration/client.config";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {BrowserAgent} from "../browser/browser.agent";
import {ModalConfig} from "../notification/modal.config";
import {RaVersion} from "../configuration/ra.version";
import {share} from "rxjs/operators";
import {ErrorType} from "../../constants";

// tslint:disable:no-bitwise
export enum MicrophoneStatus {
    NOT_INITIALIZED = 0, // no attempt to initialize audio yet
    AUDIO_NOT_AVAILABLE = 1 << 0, // not able to access needed browser functions/ no audio support in browser
    MIC_REQUESTED = 1 << 1, // request for mic access has been initiated via getUserMedia
    MIC_DENIED = 1 << 2, // user denied the mic access request
    READY = 1 << 3,
    SILENT = 1 << 4,
    BROWSER_NOT_SUPPORTED = 1 << 5,
    FILE_READ_COMPLETE = 1 << 6,
    FILE_PROCESS_COMPLETE = 1 << 7
}

// TODO: add audio interface (with format/packaging base64 vs blob)
export interface IAudioData {
    type: string;
    audio: any; // could be base64 encoded string or binary blob
    format: string; // "base64" or "blob"
    length: number; // milliseconds
}

export class WeightedDeviceInfo {
    name: string;
    id: string;
    weight: number;
}

class MozNavigator extends Navigator {
    webkitGetUserMedia: Function;
    mozGetUserMedia: Function;
}

@Injectable()
export class MicrophoneService {

    public microphoneStatus: Observable< MicrophoneStatus>; // Attach to this observable to get microphone updates.
    protected _microphoneStatusObserver: Observer<MicrophoneStatus>;
    // public audioEncoding:Observable<EncodedData>;

    private samplingRate = 16000; // sampling rate needed by recognizerWorker; input rate may be higher
    protected context;
    private node = null;
    protected consumers = [];
    private audioDevices = [];
    private DEVICE_WEIGHT = {
        WHITELIST: 10,
        BLACKLIST: -15,
        GRAYLIST: 1,
        INVALID: -15,
        NEUTRAL: 0
    };

    private audioContext;
    private currentStream: MediaStream = null;
    private tempStream: MediaStream = null;
    private _audioDeviceNameInUse: string = "";
    private audioRecordingCallback;
    private audioPlaybackCallback;
    private audioDownloadCallback;
    private mp3DataFormat: string = "base64";
    private _currentMicrophoneStatus:MicrophoneStatus = MicrophoneStatus.NOT_INITIALIZED;
    private debugAudio = true;
    private debugAudioLump = [];
    protected inputBufferLength = 4096;
    private outputBufferLength = 1600; // default value, may be overridden - size of buffer passed to recognizer
    private outputSampleRate = 16000; // default value, may be overridden
    private recording = false;
    private recordingAudioOnly = false;
    private lastWorkerPost: number = 0;
    protected inputSampleRate = 44100; // something plausible

    private config = {
        outputBufferLength: this.outputBufferLength,
        outputSampleRate: this.samplingRate,
        worker: "resources/audioRecorderWorker.js" + RaVersion.full,
        mp3Encoder: "lame.all.js" + RaVersion.full

    };

    protected worker = new Worker(this.config.worker);

    protected volumeCallback = null;

    private isMuted = false;
    private zeroData = null;

    private endRecordStatus: boolean = false;

    constructor(private audioFormat: AudioFormatService,
                private clientConfiguration: ClientConfiguration,
                private eventBus: EventBus,
                private browserAgent:BrowserAgent,
                private log:BugsnagLoggingService,
                private modalConfig: ModalConfig,
                @Inject(String) private workerPath?
                ) {

        //This is for tests, as the real worker should be loaded initially on creation.
        if (workerPath) {
          this.worker = new Worker(workerPath);
        }
        // A parent/consuming app needs to subscribe to the service, or this is called:
        // I guess it could subscribe to itself, so the _microphoneStatusObserver is not null.
        this.microphoneStatus = new Observable<MicrophoneStatus>(observer => {
            this._microphoneStatusObserver = observer;
        }).pipe(share());

        this.microphoneStatus.subscribe(status => {
            this.log.log("MICROPHONE: notice from microphone status: " + MicrophoneStatus[status]);
            this._currentMicrophoneStatus = status;
        });
        this.initSettings();
        this.initWorker();
        this.worker.onmessage = this.onWorkerMessage;
        this.audioContext = new AudioContext();
        if (this.isSafari()) {
            console.log("mic audio context state initial: " + this.audioContext.state);
            document.body.addEventListener("touchend", () =>
                this.audioContext.resume().then(() => {
                    console.log("mic audio context state after resume: " + this.audioContext.state);
                }));
        }
    }

    get isMock(): boolean {
        return false;
    }

    private onAudioHelper = (e) => {
        if (this.recording) {
            let time = new Date().getTime();
            let delta = this.lastWorkerPost ? time - this.lastWorkerPost : -1;
            this.lastWorkerPost = time;
            this.log.debug("Microphone: posting audio worker buffer of len: " + e.inputBuffer.getChannelData(0).length +
                         " msec since last:" + delta.toString() + " muted:" + this.isMuted);
            this.worker.postMessage({
                                        command: "record",
                                        buffer: [
                                            e.inputBuffer.getChannelData(0),
                                            e.inputBuffer.getChannelData(1)
                                        ],
                                        muted: this.isMuted
                                    });
        }
        if (this.recordingAudioOnly) {
            let array:Array<number> = e.inputBuffer.getChannelData(0);
            let sum = array.reduce((total, num) => { return total + num; }, 0);
            this.log.debug("Microphone: got buffer of len (recording only, will not post):"
                         + e.inputBuffer.getChannelData(0).length + " muted:" + this.isMuted + " sum:" + sum);
            if (sum === 0) {
                this._microphoneStatusObserver.next(MicrophoneStatus.SILENT);
            }
        }

    };

    protected onWorkerMessage = (e) => {
        let name;
        if (e.data.error && (e.data.error === "silent")) {
            this._microphoneStatusObserver.next(MicrophoneStatus.SILENT);
        }
        if ((e.data.command === "init")) {
            this.log.log("audio worker init completed");
        }
        if ((e.data.command === "sampleRateSet")) {
            this.log.log("sample rate set to: " + e.data.inSampleRate);
        }
        if ((e.data.command === "newBuffer")) {
            if (this.volumeCallback) {
                this.volumeCallback(e.data.volume);
            }
            this.consumers.forEach((consumer) => {
                consumer.postMessage({command: "process", data: e.data.data, endOfFile: e.data.endOfFile});
            });
            if (e.data.endOfFile) {
                this.log.log("file process complete");
                this._microphoneStatusObserver.next(MicrophoneStatus.FILE_PROCESS_COMPLETE);
            }
        }
        if ((e.data.command === "mp3Data")) {
            name = "mp3";
            this.log.log("data buf length:" + e.data.data.size + " duration:" + e.data.duration.toString());
            let mp3data: IAudioData = {
                audio: this.audioFormat.getMP3(e.data.data, this.mp3DataFormat),
                type: name, length: e.data.duration, format: this.mp3DataFormat
            };
            this.audioRecordingCallback(mp3data);
        }
        if ((e.data.command === "wavData")) {
            name = "reco";
            if (e.data.action === "normal" && this.debugAudio) {
                this.debugAudioLump.push(this.audioFormat.getWAV(e.data.data, this.outputSampleRate, "blob"));
                if (this.endRecordStatus) {
                    this.debugAudioLump.forEach(file => {
                        this.audioDownloadCallback(file, name);
                    });
                    this.debugAudioLump = [];
                }
            }
            if (e.data.action === "replay") {
                this.audioPlaybackCallback(this.audioFormat.getWAV(e.data.data, this.outputSampleRate, "blob"), name);
            }
        }
    };

    public setConsumers(consumerlist): void {
        this.consumers = consumerlist;
    }

    private listUserMedia(stream: MediaStream): void {
        navigator.mediaDevices.enumerateDevices().then((sourceInfos: MediaDeviceInfo[]) => {
            this.log.log(JSON.stringify(sourceInfos));
            this.audioDevices = this.createSortedInputs(sourceInfos, true);
            this.log.log("audio device list length: " + sourceInfos.length);

            if ((this.browserAgent.getBrowserName() === "Firefox") && stream && stream.getAudioTracks().length) {
                if (!this.getPreferredAudioDevice() ||
                    (stream.getAudioTracks()[0].label !== this.getPreferredAudioDevice().name)) {
                    this.log.log("initial device does not match requested device or is non-allowed, stopping");
                    stream.getAudioTracks()[0].stop();
                }
            }
            if (this.getPreferredAudioDevice()) {
                let constraints = {
                    video: false,
                    audio: {deviceId: {exact: this.getPreferredAudioDevice().id}}
                };
                this.log.log("calling getUserMedia with constraints: " + JSON.stringify(constraints));

                if (this.isSafari()) {
                    this.log.log("On Safari, advancing without starting user media yet");
                    this._microphoneStatusObserver.next(MicrophoneStatus.READY);
                } else {
                    this.getUserMedia(constraints,
                                      (stream: MediaStream) => {
                                          this.logMediaStreamInfo(stream);
                                          this.startUserMedia(stream, true);
                                          this.currentStream = stream;
                                      },
                                      (e) => {
                                          this.log.error("getUserMedia generated an error in listUserMedia: " + e.name,
                                              ErrorType.MICROPHONE);
                                          this.badAudioDeviceStatus(true);
                                      }
                    );
                    this._microphoneStatusObserver.next(MicrophoneStatus.MIC_REQUESTED);
                }
            } else {
                this.log.error("Audio not available, no acceptable device", ErrorType.MICROPHONE);
                this.badAudioDeviceStatus(true);
            }
        });
    }

    public annoyingGesture():void {
        if (!this.node) {
            this.startAfterEvent(this.tempStream);
        }
    }

    public safariRebootGetUserMedia(): Observable<boolean> {
        this.log.log("trying getUserMedia restart");
        if (this.currentStream) {
            this.currentStream.getAudioTracks()[0].stop();
            this.closeAudioContext();
        }
        this.audioContext = new AudioContext();
        if (this.audioContext) {
            this.log.log("mic audio context state initial: " + this.audioContext.state);
        } else {
            this.log.log("audio context is null!");
        }
        document.body.addEventListener("touchend", () => {
            if (this.audioContext.state !== "running") {
                this.audioContext.resume().then(() => {
                    this.log.log("mic audio context state after resume: " + this.audioContext.state);
                });
            }
        });

        let constraints = {
            video: false,
            audio: {deviceId: {exact: this.getPreferredAudioDevice().id}}
        };
        return Observable.create(observer => {
            this.getUserMedia(constraints, (stream) => {
                                  this.log.log("restart of getUserMedia succeeded");
                                  this.startAfterEvent(stream);
                                  observer.next(true);
                                  observer.complete();
                              },
                              (error) => {
                                  this.log.error("restart of getUserMedia failed with error: " + error.name, ErrorType.MICROPHONE);
                                  observer.next(false);
                                  observer.complete();
                              });
        });
    }

    private startAfterEvent(stream: MediaStream):void {
        console.log("START AFTER EVENT: " + stream);
        this.logMediaStreamInfo(stream);
        this.startUserMedia(stream, false);
        this.currentStream = stream;
    }

    private badAudioDeviceStatus(launchModal: boolean): void {
        this.log.log("Bad Microphone Status:");
        if (this.browserAgent.getBrowserName() === "Firefox") {
            if (this.currentStream && this.currentStream.getAudioTracks().length) {
                this.log.log("stopping track due to bad device status");
                this.currentStream.getAudioTracks()[0].stop();
            }
        }

        this.closeAudioContext();
        this.audioContext = new AudioContext();
        this._microphoneStatusObserver.next(MicrophoneStatus.AUDIO_NOT_AVAILABLE);
        if (launchModal) {
            this.modalConfig.openMessageModal("audio_device");
        }
        // reset so that user can plug in and recover
        this._microphoneStatusObserver.next(MicrophoneStatus.NOT_INITIALIZED);
        this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.reset));
    }

    private createSortedInputs(sourceInfos: MediaDeviceInfo[], verbose = false): WeightedDeviceInfo[] {
        return sourceInfos.filter(sourceInfo => sourceInfo.label && sourceInfo.kind === "audioinput")
            .map(sourceInfo => {
                let weight = this.getAudioDeviceWeight(sourceInfo.label);
                if (verbose) {
                    this.log.log("found source " + JSON.stringify(sourceInfo) + " with weight " + weight);
                }
                return <WeightedDeviceInfo>{name: sourceInfo.label, id: sourceInfo.deviceId, weight: weight};
            })
            .sort(MicrophoneService.weightComparator);
    }

    private startUserMedia(stream: MediaStream, sendEvent: boolean): void {
        // hack to prevent audio source from being garbage collected after a few seconds
        // https://bugzilla.mozilla.org/show_bug.cgi?id=934512
        // TODO: the issue requiring the firefox hack is supposedly resolved in FF 50.  Need to test
        this.log.log("got stream id: " + stream.id + " num tracks:" + stream.getAudioTracks().length);
        if (stream.getAudioTracks().length) {
            this.log.log("Got stream track 0, device is: " + stream.getAudioTracks()[0].label);
            this._audioDeviceNameInUse = stream.getAudioTracks()[0].label;
        }
        // tslint:disable-next-line:no-string-literal
        window["firefox_audio_hack"] = this.audioContext.createMediaStreamSource(stream);
        // tslint:disable-next-line:no-string-literal
        this.context = window["firefox_audio_hack"].context;
        this.inputSampleRate = this.context.sampleRate;
        this.log.log("samplerate " + this.inputSampleRate);
        this.node = this.context.createScriptProcessor(this.inputBufferLength);
        // tslint:disable-next-line:no-string-literal
        window["firefox_audio_hack"].connect(this.node);
        this.node.connect(this.context.destination);
        this.node.onaudioprocess = this.onAudioHelper;
        if (sendEvent) {
            this._microphoneStatusObserver.next(MicrophoneStatus.READY);
        } else {
            this._currentMicrophoneStatus = MicrophoneStatus.READY;
        }
        this.worker.postMessage({
                                    command: "setInputSampleRate",
                                    inputSampleRate: this.inputSampleRate
                                });
        this.log.log("mic audio context state on start: " + this.audioContext.state);
    }

    private logMediaStreamInfo(stream: MediaStream): void {
        this.log.log("stream id: " + stream.id + " active:" + stream.active);
        let audioTracks = stream.getAudioTracks();
        audioTracks.forEach((track) => {
            this.log.log("kind:" + track.kind + " label:" + track.label + " id:" + track.id);

            // @ts-ignore
            this.log.log("muted: " + track.muted + " enabled:" + track.enabled + " remote:" + track.remote);
        });
    }

    public startPostCheckWatch(): void {
        this.log.log("connect check:" + this.clientConfiguration.allowMicrophoneConnectCheck);
        if (this.clientConfiguration.allowMicrophoneConnectCheck) {
            navigator.mediaDevices.ondevicechange = () => {
                try {
                    this.testNavigatorAndAudioContextApi();
                } catch (ex) {
                    this.log.log("calling bad status with flag:" + this.recording);
                    this.badAudioDeviceStatus(this.recording);
                    if (this.recording) {
                        this.log.log("calling pause selection, this.recording = true");
                        this.eventBus.next(new PauseSelectionEvent());
                    }
                    return;
                }
                let previousDeviceName = this.currentAudioDevice() ? this.currentAudioDevice().name : "";
                this.log.log("got ondevice change on browser " + this.browserAgent.getBrowserName());
                navigator.mediaDevices.enumerateDevices().then((sourceInfos: MediaDeviceInfo[]) => {
                    this.audioDevices = this.createSortedInputs(sourceInfos, true);

                    if (!this.getPreferredAudioDevice() || this.getPreferredAudioDevice().name !== previousDeviceName) {
                        this.log.log("calling bad status with flag:" + this.recording);
                        this.badAudioDeviceStatus(this.recording);
                        if (this.recording) {
                            this.log.log("calling pause selection, this.recording = true");
                            this.eventBus.next(new PauseSelectionEvent());
                        }
                    }
                });
            };
        }
    }

    protected initWorker(): void {
        this.worker.postMessage({
            command: "init",
            config: {
                sampleRate: this.inputSampleRate,
                outputBufferLength: this.outputBufferLength,
                outputSampleRate: this.outputSampleRate,
                mp3Encoder: this.config.mp3Encoder
            }
        });
    }

    private getUserMedia(constraints: MediaStreamConstraints, success: Function, failure: Function): void {
        // if Chrome, call webkitGetUserMedia, otherwise use new promise way
        let chromeConstraints:Object = constraints;
        let trackConstraints:MediaTrackConstraints = constraints.audio as MediaTrackConstraints;

        if (trackConstraints.deviceId) {
            let deviceConstrainParameters:any = trackConstraints.deviceId;
            chromeConstraints = {video: false,
            audio: {mandatory: {sourceId: deviceConstrainParameters.exact}}};
        }
        this.log.log("constraints: " + JSON.stringify(constraints) + " chrome: " + JSON.stringify(chromeConstraints));
        if (this.browserAgent.getBrowserName() === "Chrome") {
            (<MozNavigator>navigator).webkitGetUserMedia(chromeConstraints, (stream) => {
                success(stream);
            }, (error) => {
                failure(error);
            });
        } else if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices.getUserMedia(constraints)
                .then((stream) => {
                    success(stream);
                },
                      (error) => {
                    failure(error);
                });
        } else {
            this._microphoneStatusObserver.next(MicrophoneStatus.BROWSER_NOT_SUPPORTED);
        }
    }

    public getAudioDeviceWeight(name: string): number {
        return this.isInvalidDevice(name) + this.isBlacklistedDevice(name) +
            this.isGraylistedDevice(name) + this.isWhitelistedDevice(name);
    }

    private static weightComparator(a: WeightedDeviceInfo, b: WeightedDeviceInfo): number {
        if (a.weight < b.weight) {
            return 1;
        } else if (a.weight > b.weight) {
            return -1;
        } else {
            return 0;
        }
    }

    private getPreferredAudioDevice(): WeightedDeviceInfo {
        let audioDevices = this.getAudioDevices();

        if (audioDevices.length > 0 && audioDevices[0].weight >= this.DEVICE_WEIGHT.NEUTRAL) {
            return audioDevices[0];
        }
        return null;
    }

    // This list covers our preferred USB device list, or anything that is listed as a HEADSET microphone
    private isWhitelistedDevice(currentDevice: string): number {
        if (currentDevice.indexOf("USB") >= 0 ||
            currentDevice.indexOf("AK5370") >= 0 ||
            currentDevice.indexOf("Andrea") >= 0 ||
            currentDevice.indexOf("Microsof") >= 0 ||
            currentDevice.indexOf("SPEEDLINK") >= 0 ||
            currentDevice.indexOf("<B6><FA><BB><FA>ʽ<C2><F3><BF>˷<E7>") >= 0 || // TODO: Not sure what encoding this is
            currentDevice.indexOf("Headset Microphone") >= 0) {
            return this.DEVICE_WEIGHT.WHITELIST;
        }
        return this.DEVICE_WEIGHT.NEUTRAL;
    }

    // Next level of preference is anything that is called a microphone, in various languages.  On Chromebooks we just
    // look for the device labeled Default because that is how it is named if directly plugging in the headset
    private isGraylistedDevice(currentDevice: string): number {
        if (currentDevice.indexOf("Microphone") >= 0 ||
            currentDevice.indexOf("Micrófono") >= 0 ||  // Spanish
            currentDevice.indexOf("micrófono") >= 0 ||  // Spanish, substring match for phrase such as "Varios micrófonos"
            currentDevice.indexOf("Microfone") >= 0 ||  // Portuguese
            currentDevice.indexOf("Microfoon") >= 0 ||  // Dutch
            currentDevice.indexOf("Mikrofon") >= 0 ||  // Czech
            currentDevice.indexOf("麥克風") >= 0 ||      // Chinese Traditional, Taiwan Chinese
            currentDevice.indexOf("麦克风") >= 0 ||      // Chinese Simplified
            this.isChromebookDefaultDevice(currentDevice)) {
            return this.DEVICE_WEIGHT.GRAYLIST;
        }
        return this.DEVICE_WEIGHT.NEUTRAL;
    }

    private isChromebookDefaultDevice(currentDevice: string): boolean {
        const isDefault = currentDevice.indexOf("Default") >= 0 ||
                          currentDevice.indexOf("Standaard") >= 0; // Dutch
        return isDefault && (this.browserAgent.getBrowserName() === "Chrome OS");
    }

  // Somewhat more complex rules for things we don't want to select... mainly built in microphones, other distant
  // microphones such as those that go with cameras, and things that are not microphones.
  // RA-5014: Removed (this.browserAgent.getBrowserName() !== "Chrome OS") &&
  private isInvalidDevice(currentDevice: string): number {
      // TODO: not sure why this check/if we still want it here
      if (!this.isSafari()) {
          // RA-5014 : Removed:currentDevice.indexOf("Internal Mic") >= 0
          // RA-5014 : Removed: check for currentDevice.indexOf("Built-in") >= 0
          if (currentDevice.indexOf("Stereo Mix") >= 0 ||
              currentDevice.indexOf("<C1><A2><CC><E5><C9><F9><BB><EC><D2><F4>") >= 0 ||
              currentDevice.indexOf("<C2><F3><BF>˷<E7><D5><F3><C1><D0>") >= 0 || // Think this is Korean
              currentDevice.indexOf("LogMeIn") >= 0 ||
              currentDevice.indexOf("LifeCam") >= 0 ||
              currentDevice.indexOf("Cinema") >= 0 ||
              currentDevice.indexOf("Microsoft Teams") >= 0 ||
              currentDevice.indexOf("Input") >= 0) {
              return this.DEVICE_WEIGHT.INVALID;
          }
      } else {
          return this.DEVICE_WEIGHT.WHITELIST;
      }
      return this.DEVICE_WEIGHT.NEUTRAL;
  }

    // old rule, possibly outdated, for a rogue lenovo audio card that claimed it was a USB
    private isBlacklistedDevice(currentDevice: string): number {
        if (currentDevice.indexOf("Lenovo USB") >= 0) {
            return this.DEVICE_WEIGHT.BLACKLIST;
        }
        return this.DEVICE_WEIGHT.NEUTRAL;
    }

    private testNavigatorAndAudioContextApi(): void {
        // tslint:disable-next-line:no-string-literal
        window.AudioContext = window.AudioContext || window["webkitAudioContext"];
        let log = "getUserMedia available: ";
        if (navigator.getUserMedia) {
            log += "navigator.getUserMedia ";
        }
        if ((<MozNavigator>navigator).webkitGetUserMedia) {
            log += "navigator.webkitGetUserMedia ";
        }
        if ((<MozNavigator>navigator).mozGetUserMedia) {
            log += "navigator.mozGetUserMedia ";
        }
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            log += "navigator.mediaDevices.getUserMedia ";
        }
        this.log.log(log);
    }

    public request(): void {
        try {
            this.testNavigatorAndAudioContextApi();
            if (this.currentStream) {
                this.currentStream.getAudioTracks()[0].stop();
            }
            this.closeAudioContext();
        } catch (e) {
            this.log.log("Audio not available due to API issue: ");
            this.badAudioDeviceStatus(true);
        }
        this.log.log("calling getUserMedia from request (unless safari and already have mic ready)");
        if (!(this.isSafari() && (this._currentMicrophoneStatus === MicrophoneStatus.READY))) {
            this.audioDevices = [];
            this.getUserMedia({audio: true}, (stream) => {
                this.tempStream = stream;
                this.logMediaStreamInfo(stream);
                this.listUserMedia(stream);
            }, (e) => {
                this.log.error("getUserMedia error: " + e.name, ErrorType.MICROPHONE);
                // This is the case where the user declined the mic access request:
                // Chrome and Edge return PermissionDeniedError, Firefox returns NotAllowedError
                if ((e.name === "PermissionDeniedError") || (e.name === "NotAllowedError")) {
                    this.log.log("calling permission denied modal");
                    this.modalConfig.openMessageModal("asr_microphone_deny");
                    this._microphoneStatusObserver.next(MicrophoneStatus.MIC_DENIED);
                } else {
                    this.log.log("calling no headset modal");
                    this.badAudioDeviceStatus(true);
                }

            });
        }
        this._microphoneStatusObserver.next(MicrophoneStatus.MIC_REQUESTED);
    }

    protected initSettings(config?): void {
        this.inputBufferLength = (config && config.inputBufferLength) || 4096;
        this.outputBufferLength = (config && config.outputBufferLength) || 1600;
        this.outputSampleRate = (config && config.outputSampleRate) || 16000;
        this.zeroData = new Int16Array(this.outputBufferLength);
        for (let i = 0; i < this.outputBufferLength; i++) {
            this.zeroData[i] = 0;
        }
    }

    public start(): boolean {
        this.log.log("microphoneService start recording");
        this.recording = true;

        this.worker.postMessage({command: "clearwav"});

        return (this.consumers.length > 0);

    }

    public stop(options = {clear: true}): void {
        this.log.log("microphoneService stop recording");
        if (this.recording) {
            this.consumers.forEach((consumer) => {
                consumer.postMessage({command: "stop"});
            });
            this.recording = false;

            if (options.clear) {
                this.worker.postMessage({command: "clear"}); // TBD: do we really want to lose this?
            }
        }
    }

    public startAudioOnly(): void {
        this.log.log("microphoneService start recording");
        this.recordingAudioOnly = true;

    }

    public stopAudioOnly(options = {clear: true}): void {
        this.log.log("microphoneService stop recording");
        if (this.recordingAudioOnly) {
            this.recordingAudioOnly = false;
        }
    }

    public mute(): void {
        this.isMuted = true;
    }

    public unmute(): void {
        this.isMuted = false;
    }

    public get isMute(): boolean {
        return this.isMuted;
    }

    public cancel(): void {
        this.stop();
    }

    public getAudioDevices(): WeightedDeviceInfo[] {
        return this.audioDevices;
    }

    public currentAudioDevice(): WeightedDeviceInfo {
        return this.getPreferredAudioDevice();
    }

    get audioDeviceNameInUse(): string {
        return this._audioDeviceNameInUse;
    }
    get currentMicrophoneStatus(): MicrophoneStatus {
        return this._currentMicrophoneStatus;
    }

    public requestPlayback(callback: Function): void {
        this.setAudioPlaybackCallback(callback);
        this.worker.postMessage({command: "requestreplaywavdata"});
    }

    public requestMP3(callback: Function, mp3DataFormat: string): void {
        this.setAudioRecordingCallback(callback);
        this.mp3DataFormat = mp3DataFormat;
        this.worker.postMessage({command: "requestmp3data"});

    }

    public isRecording(): boolean {
        return this.recording;
    }

    public setVolumeListener(volumeListener): void {
        this.volumeCallback = volumeListener;
    }

    public getBuffersPerSec(): number {
        return this.outputSampleRate / this.outputBufferLength;
    }

    public setAudioRecordingCallback(callback): void {
        this.audioRecordingCallback = callback;
    }

    public setAudioPlaybackCallback(callback): void {
        this.audioPlaybackCallback = callback;
    }

    // keeping this for debugging purposes
    public setAudioDownloadCallback(callback): void {
        this.audioDownloadCallback = callback;
    }

    // needed for tests (and possibly other reasons), otherwise we hit a limit and/or get into audio doubling situations
    public closeAudioContext(): void {
        if (this.node) {
            this.node.onaudioprocess = null;
            this.node.disconnect();
            this.node = null;
        }

        if (this.context && (typeof this.context.close === "function")) {
            this.context.close();
        }
        this.context = null;
    }

    public getAudioContext(): AudioContext {
        return this.audioContext;
    }

    public isSafari():boolean {
        return (this.browserAgent.getBrowserName() === "Safari");
    }

}
